import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { pluralize } from '../../utils/strings'

import Headline from '../Headline'
import Text from '../Text'

const Department = (props) => {
  const { department, isFiltered, testID } = props

  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setIsOpen(isFiltered)
  }, [isFiltered, department])

  return (
    <Wrapper className={isOpen ? 'open' : ''} data-testid={testID}>
      <HeadingContainer data-testid={'department-heading'} onClick={() => setIsOpen(!isOpen)} isOpen={isOpen}>
        <TitleContainer>
          <DepartmentTitle >{department.title}</DepartmentTitle>

          <Openings size="small">
            {`${ department.postings.length } ${ pluralize(
              department.postings.length, 'Opening'
            ) }`}
          </Openings>
        </TitleContainer>

        <Headline size="small" testID={'expand-container'}>{isOpen ? 'Collapse -' : 'Expand +'}</Headline>
      </HeadingContainer>

      <Jobs className={isOpen ? '' : 'collapsed'} data-testid={'job-container'}>
        {department.postings.map((post) => {
          return (
            <JobContainer
              href={`/careers/job?lever_jid=${ post.id }`}
              key={post.id}
              data-testid="job-link"
            >
              <JobInfo>
                <StyledText>{post.text}</StyledText>
                <StyledText>{post.categories.location}</StyledText>
              </JobInfo>

              <StyledText>View -&gt;</StyledText>
            </JobContainer>
          )
        })}
      </Jobs>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 0 20px;
  margin: 0 auto 20px;
  overflow: hidden;
  background-color: ${ (props) => props.theme.colors.white };
  border: 1px solid ${ (props) => props.theme.colors.black };
  border-radius: 10px;

  @media screen and (min-width: ${ (props) => props.theme.breakpoints.sm }) {
    max-width: 80%;
    padding: 0 48px;
  }

  &.open {
    padding-bottom: 24px;

    @media screen and (min-width: ${ (props) => props.theme.breakpoints.sm }) {
      padding-bottom: 40px;
    }
  }
`
const Jobs = styled.div`
  &.collapsed {
    display: none;
  }
`

const StyledText = styled(Text)`
  margin: 0;
`

const JobContainer = styled.a`
  align-items: center;
  justify-content: space-between;
  z-index: 1;
  display: flex;
  padding: 21px 0;
  text-align: left;
  background: none;
  border-bottom: 1px solid ${ (props) => props.theme.colors.black };
  transform: scale(1);
  transition: all 0.2s ease-in-out;

  @media screen and (min-width: ${ (props) => props.theme.breakpoints.sm }) {
    margin: 0;
  }

  @media screen and (prefers-reduced-motion: reduce) {
&::before {
    position: absolute;
    right: -48px;
    bottom: -2px;
    left: -48px;
    z-index: -1;
    height: 0;
    content: '';
    background: ${ (props) => props.theme.colors.mojito[3] };
    transition: none;
}
}

  &:last-child {
    border-bottom: none;
  }

  &::before {
    position: absolute;
    right: -48px;
    bottom: -2px;
    left: -48px;
    z-index: -1;
    height: 0;
    content: '';
    background: ${ (props) => props.theme.colors.mojito[3] };
    transition: height 0.2s cubic-bezier(0.165, 0.84, 0.44, 1);
  }

  &:hover, &:focus {
    z-index: 10;

    &::before {
      top: 0;
      height: 100%;
    }

    ${ StyledText }{
      text-decoration: underline;
    }
  }

  &::after {
    display: none;
  }
`

const JobInfo = styled.div`
  flex-basis: 70%;
`

const HeadingContainer = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  padding: 30px 0;
  text-align: left;
  border-bottom:
  ${ (props) =>
    (props.isOpen ? `1px solid ${ props.theme.colors.black }` : 'none') };

  @media screen and (min-width: ${ (props) => props.theme.breakpoints.sm }) {
    padding: 40px 0;
  }

  &:hover, &:focus {
    cursor: pointer;
  }
`

const TitleContainer = styled.div`
  flex-basis: 50%;
  flex-direction: column;
  justify-content: space-between;
  display: flex;

  @media screen and (min-width: ${ (props) => props.theme.breakpoints.sm }) {
    align-items: center;
    flex-basis: initial;
    flex-direction: row;
  }
`

const DepartmentTitle = styled(Headline)`
  margin: 0;
`

const Openings = styled(Headline)`
  text-transform: uppercase;

  @media screen and (min-width: ${ (props) => props.theme.breakpoints.sm }) {
    margin-left: 24px;
  }
`

Department.propTypes = {
  department: PropTypes.objectOf(PropTypes.any).isRequired,
  isFiltered: PropTypes.bool.isRequired,
  testID: PropTypes.string.isRequired,
}

export default Department
